import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const mapChoroplethRevealSkill: CodeoSkill = {
    name: SKILL_NAMES.mapChoroplethReveal,
    kind: 'codeo',
    description:
        'Shade countries or US states by a figure, filling them lowest to highest so the map ' +
        'warms toward the one that matters — for a real figure that differs by place.',
    checkLooksFor: 'an L5Geo map declaring data-l5-values whose script sets region fills',
    documentUsesSkill: check(
        (script, markup) =>
            /\bL5Geo\b/.test(script) &&
            /\bdata-l5-values\b/.test(markup) &&
            /\bfill\b/.test(script),
    ),
    instructions: `# Map Choropleth Reveal

A map where colour carries a figure: each place shaded by its value, the shading arriving lowest first so the map warms toward the place that matters, which lands last and is named with its figure. The order is the argument — the viewer watches the pattern form rather than decoding a finished one.

Use it when the figure is about *where* — sales by state, adoption by country — and the geography itself says something (a coast, a cluster, a gap). For one place, \`${SKILL_NAMES.mapLocateZoom}\` is clearer; for a ranking whose geography does not matter, \`${SKILL_NAMES.statBarsAndFills}\` reads faster.

The map comes from \`L5Geo\`, which the host injects: every d3-geo projection and path generator, plus the world's countries and the US states. Never type a path's \`d\` — generate it.

## Markup

The figures go on the svg as \`data-l5-values\`, in the same order as the places in \`data-l5-regions\` — the same numbers the plan carries in its \`data\`.

\`\`\`html
<div class="absolute inset-0 flex items-center gap-28 py-24 pr-24 pl-60">
  <div class="flex flex-col gap-10 max-w-3xl min-w-0" style="font-family: var(--l5-font-0), sans-serif;">
    <h1 id="title" style="margin: 0; font-size: 88px; font-weight: 800; line-height: 1.05; color: var(--l5-color-4, #f8fafc);">Where our stores are</h1>
    <div id="legend" class="flex flex-col gap-4">
      <div id="legend-ramp" style="width: 420px; height: 20px; border-radius: 10px;"></div>
      <div class="flex justify-between" style="width: 420px; font-size: 52px; font-weight: 600; color: var(--l5-color-4, #f8fafc);">
        <span>9</span><span>48</span>
      </div>
    </div>
  </div>
  <div class="shrink-0">
    <svg id="states" width="960" height="600" viewBox="0 0 960 600"
         data-l5-values="[48, 41, 33, 29, 18, 15, 12, 9]"
         data-l5-regions='["CA", "TX", "NY", "FL", "IL", "WA", "GA", "CO"]'>
      <g id="state-shapes" data-l5-allow-overlap fill="var(--l5-color-1, #94a3b8)" fill-opacity="0.25"
         stroke="var(--l5-color-0, #0f172a)" stroke-width="1.5" stroke-linejoin="round"></g>
      <text id="leader-value" text-anchor="middle" dominant-baseline="central"
            font-family="var(--l5-font-0), sans-serif" font-size="56" font-weight="800"
            fill="var(--l5-color-0, #0f172a)">48</text>
    </svg>
  </div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const svg = document.querySelector('#states');
const group = document.querySelector('#state-shapes');
const values = JSON.parse(svg.dataset.l5Values);
const regions = JSON.parse(svg.dataset.l5Regions);

const projection = L5Geo.geoAlbersUsa().fitExtent([[8, 8], [952, 592]], L5Geo.usNation());
const path = L5Geo.geoPath(projection);
const shapeFor = (feature) => {
  const shape = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  shape.setAttribute('d', path(feature));
  return group.appendChild(shape);
};
// Albers USA has no place for the territories and projects them to null.
L5Geo.usStates().features.filter((feature) => path(feature)).map(shapeFor);

// One hue, from the background toward the accent, so a larger figure always stands further out
// from the background whatever the palette. The legend is painted from the same ramp, so the key
// cannot disagree with the map.
const palette = getComputedStyle(document.documentElement);
const background = palette.getPropertyValue('--l5-color-0').trim() || '#0f172a';
const accent = palette.getPropertyValue('--l5-color-3').trim() || '#f59e0b';
const ramp = (t) => gsap.utils.interpolate(background, accent, 0.35 + 0.65 * t);
document.querySelector('#legend-ramp').style.background = \`linear-gradient(90deg, \${ramp(0)}, \${ramp(1)})\`;

const min = Math.min(...values), max = Math.max(...values);
const shaded = regions.map((region, i) => ({
  shape: shapeFor(L5Geo.usState(region)),
  value: values[i],
  colour: ramp((values[i] - min) / (max - min)),
  centre: path.centroid(L5Geo.usState(region)),
}));

// Lowest first, so the map warms toward the figure that matters and lands it last.
const ordered = [...shaded].sort((a, b) => a.value - b.value);
const leader = ordered[ordered.length - 1];
const leaderValue = document.querySelector('#leader-value');
leaderValue.setAttribute('x', leader.centre[0]);
leaderValue.setAttribute('y', leader.centre[1]);

gsap.set('#title', { opacity: 0, y: 24 });
gsap.set(['#state-shapes', '#legend'], { opacity: 0 });
gsap.set(ordered.map((entry) => entry.shape), { fillOpacity: 0 });
gsap.set(leaderValue, { opacity: 0, scale: 0.6, transformOrigin: 'center center' });

tl.to('#title', { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }, 0.2);
tl.to('#state-shapes', { opacity: 1, duration: 0.8, ease: 'power2.out' }, 0.5);
ordered.map((entry, i) =>
  tl.to(entry.shape, { fill: entry.colour, fillOpacity: 1, duration: 0.5, ease: 'power2.out' }, 1.3 + i * 0.18),
);
tl.to('#legend', { opacity: 1, duration: 0.5, ease: 'power2.out' }, 1.6);
tl.to(leaderValue, { opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(2)' }, 1.3 + ordered.length * 0.18 + 0.3);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Countries across the world**: \`L5Geo.geoNaturalEarth1()\` fitted to \`L5Geo.countries()\` with Antarctica filtered out, and \`L5Geo.country('Germany')\` to find each one — everyday names, ISO numeric codes and accented or unaccented spellings all resolve.
- **The contiguous states only**: \`usStates()\` carries Alaska, Hawaii, Puerto Rico and the island territories. Keep the features with \`Number(state.id) < 60\` other than Alaska and Hawaii, fit \`L5Geo.geoAlbers()\` to them, and draw only those — Albers USA would still draw Alaska and Hawaii as insets, and under plain Albers Puerto Rico lands in the frame's corner.
- **One continent or region**: fit the projection to the area itself, given as the outline of a longitude/latitude box — \`.fitExtent(box, L5Geo.geoGraticule().extent([[-10, 36], [30, 70]]).outline())\` for Europe, which follows the box's curved edges where four corner points would let them bulge out of the frame — and project it with an equal-area projection centred on it, \`L5Geo.geoAzimuthalEqualArea().rotate([-10, -52])\`. Countries reach past that box, so make the svg the whole frame (\`absolute inset-0\`, \`viewBox="0 0 1920 1080"\`), fit the box into the part of the frame the map should fill, and put \`data-l5-allow-clip data-l5-allow-offscreen\` beside \`data-l5-allow-overlap\` on the land group; the frame edge is then the crop. An svg sitting in a cell crops the map at its own box, a hard rectangle cut through the land.
- **Two-sided figures** (change, growth that can be negative): ramp from one accent through the land colour to a second accent, with the land colour at zero, so a fall and a rise read as opposites rather than as less and more of one thing.
- **A sweep instead of a ranking**: fill west to east (sort by \`path.centroid\`'s x) when the story is spread — "it started on the coast and moved inland" — rather than size.
- **Name more than the leader**: at most three figures on the map, each inside its own place. Anything beyond that belongs in a list beside the map, or the map turns into a table laid over a drawing.

## Pitfalls

- **Declare the figures** as \`data-l5-values\` and compute every colour from them. A colour picked by eye is a figure nobody can check, and the legend then disagrees with the map.
- **Fit to the area you mean, never to the countries.** A country's shape includes all of its territory: France carries French Guiana, so a projection fitted to France and its neighbours frames half the Atlantic, and Europe comes out a small cluster with a lone shape in mid-ocean. The US carries Alaska and Hawaii, which is why the US uses Albers USA.
- **One hue, rising away from the background.** A ramp between two unrelated colours passes through a muddy middle that reads as a third category, and a ramp that ends on a colour close to the background makes the largest figure the faintest place on the map. Interpolate from the background toward one accent, and keep unshaded land faint, so the shaded places are the strongest things on the map and the largest is the strongest of them.
- **Keep the title up to the end.** The final frame is the one the viewer is left with, and the title is what says what the figures count — a map ending on "640" alone says nothing. The leader's figure arrives alongside the title, never in its place.
- **The legend carries the figures.** Its two ends are labelled with the lowest and highest values, as above; two coloured swatches with no numbers tell the viewer nothing.
- **Scale an SVG mark about a map point with \`svgOrigin\`.** \`svgOrigin: x + ' ' + y\` takes the point in the svg's own units; \`transformOrigin\` in px is measured from the element's own box, so a dot scaled about a map coordinate that way swings off its place.
- **Keep the shaded places to about eight.** Past that the viewer cannot match shades to the legend at video pace; group the rest or leave them unshaded.
- **Small places vanish.** At a whole-US or whole-world view, Rhode Island, Delaware or Luxembourg is a few pixels, so a figure shaded there is invisible. If one of them matters, call it out with a label and a leader line, or choose a projection that frames it.
- **The atlas is 1:110m — 177 countries**, and microstates (Singapore, Malta, Bahrain) are not in it. An unknown name throws, naming the closest ones it has, and a throw stops the script, which lint reports as the timeline never being registered. When that appears on a map, check every name passed to \`L5Geo\` first.
- **Put the svg inside a wrapping div** when it sits in a flex row, as above, so the row's layout is measured from what the map draws.
- **Name script variables away from the window's own properties.** A top-level \`const top\`, \`name\` or \`parent\` collides with the global of the same name and the script fails to load.`,
};
